import React, { useState } from 'react';
import { getBoundData } from '../chartDataBindings';
import { TpChartProps } from '../interfaces';
import styles from './colorLegendCategorical.module.scss';
import { generateChartTitle, getHighlightedText } from '../../helpers/misc';
import { CATEGORICAL_ORDERS, TpCategoryInfo } from '../../helpers/categoricalSorters';
import { createCategories, getCategoriesAsBlob } from '../../helpers/categoricalTools';
import { createColorMappingInfo, getMappedColorStringCategorical } from '../../helpers/colorMappingInfo';
import SearchField from '../../../../components/search-field/SearchField';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { getCategoricalEnFilterStep } from '../../../../data/selection/selectionEnFilterCategorical';
import { getSelectionHighlightColor } from '../../../themes/themes';
import { downloadBlobAsFile } from '../../helpers/downloadHelpers';
import { useExportFilename } from '../../hooks/useExportFilename';

export function ColorLegendCategorical({
  chartTypeManifest,
  dataContext,
  dataBinding,
  config,
  widgetContext,
}: TpChartProps) {
  const [searchString, setSearchString] = useState('');
  const { getFilename } = useExportFilename();

  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const colInfo = boundData.bindings.categories.columnInfo;
  const catData = boundData.bindings.categories.data;
  const itemIdData = boundData.bindings.itemId.data;

  const sortBy = config.sortBy ?? CATEGORICAL_ORDERS.Count; // Default sort order
  const categoriesMapping = createCategories(boundData.canonicalTypeInfo, catData, colInfo, null, itemIdData, sortBy);
  const categoriesData = categoriesMapping.categories;

  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;

  const colorMappingInfo = createColorMappingInfo(config, boundData, 'categories', 1, globalColorMappingConfig);

  const filterInfo = getCategoricalEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    colInfo.id,
  );

  function onItemClicked(selectedCategoryId: any) {
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.CATEGORICAL,
      dataTableId: boundData.dataTable.id,
      colId: colInfo.id,
      selectedCategoryId,
    });
  }

  const categoriesDataFiltered = [];
  let maxCount = 1;
  for (let catIdx = 0; catIdx < categoriesData.length; catIdx++) {
    const cat = categoriesData[catIdx];
    maxCount = Math.max(maxCount, cat.count);
    let inMatch = true;
    if (searchString) {
      inMatch = false;
      if (cat.displayString.toLowerCase().indexOf(searchString.toLowerCase()) >= 0) inMatch = true;
    }
    if (inMatch) categoriesDataFiltered.push(cat);
  }

  function renderColorBar(cat: TpCategoryInfo) {
    const colorStr = getMappedColorStringCategorical(colorMappingInfo, cat.value);
    return (
      <div className={styles.colorBar}>
        <div className={styles.colorDot} style={{ backgroundColor: colorStr }} />
        <div
          className={styles.bar}
          style={{ backgroundColor: 'rgba(0,0,0,0.1)', width: `${(cat.count * 100) / maxCount}%` }}
        />
        <div
          className={styles.bar}
          style={{ backgroundColor: colorStr, width: `${(cat.countSelected * 100) / maxCount}%` }}
        />
      </div>
    );
  }

  const rows = categoriesDataFiltered.map((row: TpCategoryInfo, rowIdx: number) => (
    <div
      key={`rw_${rowIdx}`}
      className={styles.categoryLine}
      onClick={() => {
        onItemClicked(row.value);
      }}
    >
      <div className={styles.categoryText}>
        {renderColorBar(row)}
        {getHighlightedText(row.displayString, searchString)}
        {filterInfo && filterInfo.selectedCategoryIdList.includes(row.value) && (
          <div
            className={styles.filterSelectionIndicator}
            style={{ background: getSelectionHighlightColor().lighten(0.25).toCanvasString() }}
          />
        )}
      </div>
    </div>
  ));

  const title = generateChartTitle(dataContext, dataBinding, config, colInfo.name);

  // Widget data download is a CSV of the categories, not a plot image
  const downloadCategoriesTable = () => {
    const blobData = getCategoriesAsBlob(categoriesData);
    const fileName = getFilename(
      {
        assetId: dataContext.assetId,
        dataType: chartTypeManifest.id,
        title,
      },
      'csv',
    );
    downloadBlobAsFile(blobData, fileName);
  };

  if (widgetContext.setBitmapDownloadFunction) widgetContext.setBitmapDownloadFunction(downloadCategoriesTable);

  return (
    <>
      <div className={styles.header}>{title}</div>
      <div className={styles.listWrapper}>{rows}</div>
      <div className={styles.footer}>
        <div style={{ width: 'calc(100% - 110px)' }}>
          <SearchField
            updateSearchString={(newVal: string) => {
              setSearchString(newVal);
            }}
          />
        </div>
      </div>
    </>
  );
}
